import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, ArrowRight, Wind, Images } from 'lucide-react';
import PhotoCapture from '../PhotoCapture';
import PhotoGalleryModal from '../PhotoGalleryModal';
import { PhotoData } from '../../services/CameraService';
import { RetrofitSurveyData, getRetrofitSurveyAddress } from '../../utils/retrofitSurveyStorage';
import { useRetrofitPhotoUpload } from '../../hooks/useRetrofitPhotoUpload';
import { analyzeSectionFileSize, logFileSizeAnalysis } from '../../utils/fileSizeAnalysis';
import { deletePhotoFromSupabase } from '../../utils/supabaseStorage';

// --- INTERFACES ---
export interface VentilationConditionSurveyFormProps {
  onNavigateNext: () => void;
  onNavigateBack: () => void;
  floorPlanId?: string;
  surveyData?: RetrofitSurveyData;
  surveyId?: string;
  onDataUpdate?: (updater: (prev: RetrofitSurveyData) => RetrofitSurveyData, sectionId?: string) => void;
  onSaveImmediately?: () => Promise<string>;
  onGalleryStateChange?: (isOpen: boolean) => void;
}

interface DisplayablePhotoData extends PhotoData {
  displayId: string;
  dataUrl: string;
  fileName?: string;
  url?: string;
  uploading?: boolean;
  error?: string;
}

interface VentilationData {
  // Room Counts
  habitableRooms: string;
  heatedHabitableRooms: string;
  totalRooms: string;
  // Windows
  windowFramesCondition: string;
  draughtProofingCondition: string;
  windowFrameType: string;
  numberOfWindows: string;
  numberOfTrickleVents: string;
  windowGlazingType: string;
  windowGlazingMeasurement: string;
  windowGlazingThicknessImage: DisplayablePhotoData[];
  // Doors
  numberOfExternalDoors: string;
  externalDoorsCondition: string;
  externalDoorsType: string;
  draughtProofingOnExternalDoor: string;
  doorUndercutsPresent: boolean | null;
  roomsWithNoDoorUndercuts: string;
  externalDoorsImages: DisplayablePhotoData[];
  frontDoorOpenImage: DisplayablePhotoData[];
  // Ventilation Fans
  numberOfWetRooms: string;
  numberOfVentilationFans: string;
  roomsMissingVentilationFans: string;
  ventilationTypePresent: string[];
  ventilationTypeCounts: {
    'decentralised_mechanical': number;
    'intermittent': number;
    'centralised_mechanical': number;
    'other': number;
  };
  // Roof
  roofType: string;
  externalRoofCondition: string;
  visibleExternalRoofDefects: boolean | null;
  roofDefectImages: DisplayablePhotoData[];
  // Internal Walls
  internalWallCondition: string;
  evidenceOfDamp: boolean | null;
  dampnessImages: DisplayablePhotoData[];
  // Floor
  floorConstructionType: string;
  adequateSubFloorVentilation: boolean | null;
  evidenceOfFloorDampness: boolean | null;
  // External Walls
  externalWallType: string;
  externalWallCondition: string;
  additionalVisibleWallDefects: boolean | null;
  wallDefectImages: DisplayablePhotoData[];
}


// --- OPTIONS ---
const conditionOptions = [
  { value: "", label: "Select Condition" },
  { value: "good", label: "Good" },
  { value: "average", label: "Average" },
  { value: "poor", label: "Poor" },
  { value: "not-applicable", label: "Not Applicable" },
];

const windowFrameOptions = [
    { value: "", label: "Select Type" },
    { value: "pvc", label: "PVC" },
    { value: "wood", label: "Wood" },
    { value: "metal", label: "Metal" },
];

const externalDoorTypeOptions = [
    { value: "", label: "Select Type" },
    { value: "pvc", label: "PVC" },
    { value: "wood", label: "Wood" },
    { value: "composite", label: "Composite" },
    { value: "metal", label: "Metal" },
];

const roofTypeOptions = [
    { value: "", label: "Select Roof Type"},
    { value: "pitched", label: "Pitched" },
    { value: "flat", label: "Flat" },
    { value: "thatched", label: "Thatched" },
];

const ventilationTypeOptions = [
    { value: "passive-vents", label: "Passive Vents" },
    { value: "intermittent-extract-fans", label: "Intermittent Extract Fans" },
    { value: "continuous-extract-fans", label: "Continuous-extract fans" },
    { value: "positive-input-ventilation", label: "Positive Input Ventilation" },
    { value: "whole-house-mechanical", label: "Whole-house Mechanical" }
];

const internalWallConditionOptions = [
    { value: "", label: "Select Condition" },
    { value: "good", label: "Good" },
    { value: "average", label: "Average" },
    { value: "poor", label: "Poor" },
];

const floorConstructionOptions = [
    { value: "", label: "Select Type" },
    { value: "suspended-timber", label: "Suspended Timber" },
    { value: "solid-concrete", label: "Solid Concrete" },
];

const externalWallTypeOptions = [
  { value: "", label: "Select Wall Type" },
  { value: "conventional_cavity", label: "Conventional, not high-rise, not protected - cavity wall" },
  { value: "traditional_solid", label: "Traditional, not protected - solid wall" },
  { value: "system_solid", label: "System-built, not high-rise, not protected - solid wall" },
  { value: "high_rise_cavity", label: "High rise - cavity wall" },
  { value: "high_rise_solid", label: "High rise - solid wall" },
  { value: "protected_cavity", label: "Protected - cavity wall" },
  { value: "protected_solid", label: "Protected - solid wall" },
  { value: "timber_frame", label: "Timber Frame" },
];

// --- COMPONENT ---
const VentilationConditionSurveyForm: React.FC<VentilationConditionSurveyFormProps> = ({
  onNavigateNext,
  onNavigateBack,
  floorPlanId,
  surveyData,
  surveyId,
  onDataUpdate,
  onGalleryStateChange,
}) => {
  // Initialize photo upload queue with explicit surveyId and property name for better file organization
  const propertyName = surveyData ? getRetrofitSurveyAddress(surveyData) : undefined;
  const { queuePhotoUpload } = useRetrofitPhotoUpload(
    surveyId || surveyData?.id || floorPlanId || undefined,
    propertyName
  );

  const [formData, setFormData] = useState<VentilationData>({
    habitableRooms: surveyData?.ventilation_condition?.habitableRooms || surveyData?.initial_questionnaire?.habitableRooms || '',
    heatedHabitableRooms: surveyData?.ventilation_condition?.heatedHabitableRooms || surveyData?.initial_questionnaire?.heatedHabitableRooms || '',
    totalRooms: surveyData?.ventilation_condition?.totalRooms || surveyData?.initial_questionnaire?.totalRooms || '',
    windowFramesCondition: surveyData?.ventilation_condition?.naturalVentilation || '',
    draughtProofingCondition: surveyData?.ventilation_condition?.mechanicalVentilation || '',
    windowFrameType: surveyData?.ventilation_condition?.airTightnessAssessment || '',
    numberOfWindows: surveyData?.ventilation_condition?.moistureIssues || '',
    numberOfTrickleVents: surveyData?.ventilation_condition?.condensationProblems || '',
    windowGlazingType: surveyData?.ventilation_condition?.windowGlazingType || '',
    windowGlazingMeasurement: surveyData?.ventilation_condition?.windowGlazingMeasurement || '',
    windowGlazingThicknessImage: [],
    numberOfExternalDoors: surveyData?.ventilation_condition?.ventilationEffectiveness || '',
    externalDoorsCondition: '',
    externalDoorsType: '',
    draughtProofingOnExternalDoor: '',
    doorUndercutsPresent: null,
    roomsWithNoDoorUndercuts: '',
    externalDoorsImages: [],
    frontDoorOpenImage: [],
    numberOfWetRooms: '',
    numberOfVentilationFans: '',
    roomsMissingVentilationFans: '',
    ventilationTypePresent: [],
    ventilationTypeCounts: {
      'decentralised_mechanical': 0,
      'intermittent': 0,
      'centralised_mechanical': 0,
      'other': 0
    },
    roofType: '',
    externalRoofCondition: '',
    visibleExternalRoofDefects: null,
    roofDefectImages: [],
    internalWallCondition: '',
    evidenceOfDamp: null,
    dampnessImages: [],
    floorConstructionType: '',
    adequateSubFloorVentilation: null,
    evidenceOfFloorDampness: null,
    externalWallType: '',
    externalWallCondition: '',
    additionalVisibleWallDefects: null,
    wallDefectImages: [],
  });

  // Gallery state for photo modal
  const [selectedGallery, setSelectedGallery] = useState<'roofDefectImages' | 'dampnessImages' | 'wallDefectImages' | 'externalDoorsImages' | 'frontDoorOpenImage' | 'windowGlazingThicknessImage' | null>(null);

  // Debounce timer for dropdown autosave
  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Track recent changes to prevent sync during immediate autosaves
  const recentChangeRef = useRef(false);
  // Track last synced data to prevent loops
  const lastSyncedDataRef = useRef<string>('');
  
  // CDN Migration: Migrate existing base64 photos to CDN
  const migratedPhotoIdsRef = useRef<Set<string>>(new Set());
  const migrationRunOnceRef = useRef(false);

  useEffect(() => {
    // Only run migration once on mount
    if (migrationRunOnceRef.current) return;
    migrationRunOnceRef.current = true;

    console.log('[VentilationConditionSurvey] 🔄 Checking for base64 photos to migrate to CDN');

    let totalPhotosChecked = 0;
    let hasBase64Photos = false;

    const photoFields: Array<'roofDefectImages' | 'dampnessImages' | 'wallDefectImages' | 'externalDoorsImages' | 'frontDoorOpenImage' | 'windowGlazingThicknessImage'> = [
      'roofDefectImages', 'dampnessImages', 'wallDefectImages', 'externalDoorsImages', 'frontDoorOpenImage', 'windowGlazingThicknessImage'
    ];

    photoFields.forEach(photoType => {
      const photoArray = formData[photoType];
      if (!Array.isArray(photoArray)) return;

      photoArray.forEach(photo => {
        totalPhotosChecked++;

        if (migratedPhotoIdsRef.current.has(photo.displayId) || photo.uploading) {
          return;
        }

        const hasBase64Data = photo.dataUrl && photo.dataUrl.startsWith('data:image/');
        const lacksUrl = !photo.url || photo.url === '';

        if (hasBase64Data && lacksUrl) {
          hasBase64Photos = true;
          migratedPhotoIdsRef.current.add(photo.displayId);

          console.log(`[VentilationConditionSurvey] 🔄 Migrating ${photoType} photo to CDN:`, photo.displayId);

          queuePhotoUpload(
            photo.displayId,
            photo.dataUrl,
            'ventilation_condition',
            photoType,
            (url: string) => {
              setFormData(current => ({
                ...current,
                [photoType]: current[photoType].map(p =>
                  p.displayId === photo.displayId
                    ? { ...p, url, uploading: false, error: undefined, fileName: url }
                    : p
                )
              }));
              console.log(`[VentilationConditionSurvey] ✅ Migration complete: ${photo.displayId} → CDN URL`);
            },
            (error: string) => {
              console.error(`[VentilationConditionSurvey] ⚠️ Migration failed for ${photo.displayId}:`, error);
            }
          );
        }
      });
    });

    console.log(`[VentilationConditionSurvey] 📊 Migration check complete: ${totalPhotosChecked} photos checked, ${hasBase64Photos ? 'migration queued' : 'no migration needed'}`);
  }, [formData.roofDefectImages, formData.dampnessImages, formData.wallDefectImages, formData.externalDoorsImages, formData.frontDoorOpenImage, formData.windowGlazingThicknessImage, queuePhotoUpload]);
  
  // Sync surveyData prop changes back to form state (but not while autosave is pending or recently changed)
  useEffect(() => {
    // Don't sync if we have a pending autosave or just made a change
    if (surveyData?.ventilation_condition && !autosaveTimerRef.current && !recentChangeRef.current) {
      // Only update if data actually changed to prevent loop
      const incoming = JSON.stringify(surveyData.ventilation_condition);
      
      if (incoming !== lastSyncedDataRef.current) {
        lastSyncedDataRef.current = incoming;
        setFormData(prev => ({
          ...prev,
          ...surveyData.ventilation_condition,
          // Ensure photo arrays are properly typed
          roofDefectImages: Array.isArray(surveyData.ventilation_condition?.roofDefectImages) 
            ? surveyData.ventilation_condition.roofDefectImages.map((p: any) => ({ ...p, displayId: p.displayId, dataUrl: p.dataUrl || p.url }))
            : prev.roofDefectImages,
          dampnessImages: Array.isArray(surveyData.ventilation_condition?.dampnessImages) 
            ? surveyData.ventilation_condition.dampnessImages.map((p: any) => ({ ...p, displayId: p.displayId, dataUrl: p.dataUrl || p.url }))
            : prev.dampnessImages,
          wallDefectImages: Array.isArray(surveyData.ventilation_condition?.wallDefectImages) 
            ? surveyData.ventilation_condition.wallDefectImages.map((p: any) => ({ ...p, displayId: p.displayId, dataUrl: p.dataUrl || p.url }))
            : prev.wallDefectImages,
          externalDoorsImages: Array.isArray(surveyData.ventilation_condition?.externalDoorsImages) 
            ? surveyData.ventilation_condition.externalDoorsImages.map((p: any) => ({ ...p, displayId: p.displayId, dataUrl: p.dataUrl || p.url }))
            : prev.externalDoorsImages,
          frontDoorOpenImage: Array.isArray(surveyData.ventilation_condition?.frontDoorOpenImage) 
            ? surveyData.ventilation_condition.frontDoorOpenImage.map((p: any) => ({ ...p, displayId: p.displayId, dataUrl: p.dataUrl || p.url }))
            : prev.frontDoorOpenImage,
          windowGlazingThicknessImage: Array.isArray(surveyData.ventilation_condition?.windowGlazingThicknessImage) 
            ? surveyData.ventilation_condition.windowGlazingThicknessImage.map((p: any) => ({ ...p, displayId: p.displayId, dataUrl: p.dataUrl || p.url }))
            : prev.windowGlazingThicknessImage,
        }));
      }
    }
  }, [surveyData]);

  // 📊 FILE SIZE ANALYSIS: Photo fields for ventilation_condition section
  const VENTILATION_PHOTO_FIELDS = [
    'roofDefectImages',
    'dampnessImages',
    'wallDefectImages',
    'externalDoorsImages',
    'frontDoorOpenImage',
    'windowGlazingThicknessImage'
  ];

  // 📊 FILE SIZE ANALYSIS: Analyze ventilation_condition section
  const analyzeVentilationFileSize = useCallback(() => {
    const result = analyzeSectionFileSize(
      'ventilation_condition',
      surveyData?.ventilation_condition,
      VENTILATION_PHOTO_FIELDS
    );
    logFileSizeAnalysis('ventilation_condition', result, 'cleanupVentilationBase64');
  }, [surveyData?.ventilation_condition]);

  // Run analysis on data load
  useEffect(() => {
    if (surveyData?.ventilation_condition) {
      const timer = setTimeout(() => analyzeVentilationFileSize(), 1000);
      return () => clearTimeout(timer);
    }
  }, [surveyData?.ventilation_condition, analyzeVentilationFileSize]);

  // Expose to window for manual testing
  useEffect(() => {
    (window as any).analyzeVentilation = analyzeVentilationFileSize;
    return () => {
      delete (window as any).analyzeVentilation;
    };
  }, [analyzeVentilationFileSize]);

  // 🧹 CLEANUP: Strip base64 from photos that already have CDN URLs
  const cleanupBase64FromCDNPhotos = useCallback(() => {
    console.log('🧹 [BASE64 CLEANUP - VENTILATION] Checking for photos with CDN URLs that still have base64...');
    
    let cleanupNeeded = false;
    const cleanedFormData = { ...formData };
    
    // Clean photo fields
    VENTILATION_PHOTO_FIELDS.forEach(fieldName => {
      const photoArray = formData[fieldName as keyof VentilationData] as DisplayablePhotoData[];
      if (!Array.isArray(photoArray)) return;
      
      const cleaned = photoArray.map((photo: any) => {
        if (photo && photo.url && photo.url.startsWith('http') && (photo.dataUrl || photo.base64String)) {
          console.log(`🧹 [BASE64 CLEANUP - VENTILATION] Stripping base64 from ${fieldName}/${photo.displayId}`);
          cleanupNeeded = true;
          return {
            displayId: photo.displayId,
            format: photo.format,
            fileName: photo.fileName,
            url: photo.url,
            uploading: false,
            error: undefined,
            dataUrl: '',
            base64String: ''
          };
        }
        return photo;
      });
      
      (cleanedFormData as any)[fieldName] = cleaned;
    });
    
    if (cleanupNeeded) {
      console.log('🧹 [BASE64 CLEANUP - VENTILATION] ✅ Cleaned photos, updating state and saving...');
      setFormData(cleanedFormData);
      
      // Save cleaned data to database
      if (onDataUpdate) {
        setTimeout(() => {
          console.log('🧹 [BASE64 CLEANUP - VENTILATION] 💾 Saving cleaned photos to database');
          onDataUpdate(prevData => ({
            ...prevData,
            ventilation_condition: {
              ...prevData.ventilation_condition,
              ...cleanedFormData
            }
          }), 'ventilation_condition');
        }, 100);
      }
    } else {
      console.log('🧹 [BASE64 CLEANUP - VENTILATION] No cleanup needed, all photos are clean');
    }
  }, [formData, onDataUpdate]);

  // Expose cleanup function to window
  useEffect(() => {
    (window as any).cleanupVentilationBase64 = cleanupBase64FromCDNPhotos;
    return () => {
      delete (window as any).cleanupVentilationBase64;
    };
  }, [cleanupBase64FromCDNPhotos]);

  // 🧹 AUTO-CLEANUP: Automatically clean base64 from photos with CDN URLs on page load
  useEffect(() => {
    const allPhotos = [
      ...formData.roofDefectImages,
      ...formData.dampnessImages,
      ...formData.wallDefectImages,
      ...formData.externalDoorsImages,
      ...formData.frontDoorOpenImage,
      ...formData.windowGlazingThicknessImage
    ];
    
    if (!allPhotos.length) return;
    
    // Check if any photos have CDN URLs but still have base64
    const needsCleanup = allPhotos.some((p: any) => 
      p && p.url && p.url.startsWith('http') && (p.dataUrl || p.base64String)
    );
    
    if (needsCleanup) {
      console.log('🧹 [AUTO-CLEANUP - VENTILATION] Detected photos with CDN + base64, running automatic cleanup in 3 seconds...');
      const timer = setTimeout(() => {
        cleanupBase64FromCDNPhotos();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [formData.roofDefectImages, formData.dampnessImages, formData.wallDefectImages, formData.externalDoorsImages, formData.frontDoorOpenImage, formData.windowGlazingThicknessImage, cleanupBase64FromCDNPhotos]);

  // Auto-calculate room counts and window counts from room_details
  useEffect(() => {
    console.log('🚪 [VentilationForm] useEffect triggered:', {
      hasRooms: !!surveyData?.room_details?.rooms,
      roomsCount: surveyData?.room_details?.rooms?.length,
      autosaveTimerActive: !!autosaveTimerRef.current,
      recentChangeActive: !!recentChangeRef.current,
      willRun: !!(surveyData?.room_details?.rooms && !autosaveTimerRef.current && !recentChangeRef.current)
    });
    
    if (surveyData?.room_details?.rooms && !autosaveTimerRef.current && !recentChangeRef.current) {
      const rooms = surveyData.room_details.rooms;
      
      console.log('🚪 [VentilationForm] Rooms data:', rooms.map((r: any) => ({
        id: r.id,
        name: r.name,
        doorPresent: r.doorPresent,
        doorUndercutSize: r.doorUndercutSize
      })));
      
      // Calculate room counts
      const totalRoomsCount = rooms.length;
      const habitableRoomsCount = rooms.filter((room: any) => room.roomType === 'Habitable').length;
      const heatedHabitableRoomsCount = rooms.filter((room: any) => 
        room.roomType === 'Habitable' && room.heatingStatus === 'Heated'
      ).length;
      
      // Calculate window count from windowDetails arrays
      let totalWindowsCount = 0;
      let totalTrickleVentsCount = 0;
      rooms.forEach((room: any) => {
        if (room.windowDetails && Array.isArray(room.windowDetails)) {
          totalWindowsCount += room.windowDetails.length;
          // Sum up trickle vents from each window
          room.windowDetails.forEach((window: any) => {
            if (typeof window.trickleVents === 'number') {
              totalTrickleVentsCount += window.trickleVents;
            }
          });
        }
      });
      
      // Calculate ventilation fan data
      const wetRooms = rooms.filter((room: any) => room.roomType === 'Wet Room');
      const wetRoomsCount = wetRooms.length;
      const roomsWithVentilationFans = rooms.filter((room: any) => room.hasVentilationFan === true);
      const ventilationFansCount = roomsWithVentilationFans.length;
      
      // Find wet rooms missing ventilation fans
      const wetRoomsMissingVentilation = wetRooms
        .filter((room: any) => room.hasVentilationFan !== true)
        .map((room: any) => room.name)
        .filter(Boolean);
      const roomsMissingVentilationFansText = wetRoomsMissingVentilation.join(', ');
      
      // Collect unique ventilation types present and count each type
      const ventilationTypes = new Set<string>();
      const typeCounts: any = {
        'decentralised_mechanical': 0,
        'intermittent': 0,
        'centralised_mechanical': 0,
        'other': 0
      };
      
      roomsWithVentilationFans.forEach((room: any) => {
        if (room.ventilationFanType && room.ventilationFanType !== 'none' && room.ventilationFanType !== '') {
          ventilationTypes.add(room.ventilationFanType);
          // Count each type
          if (typeCounts.hasOwnProperty(room.ventilationFanType)) {
            typeCounts[room.ventilationFanType]++;
          }
        }
      });
      const ventilationTypesPresentArray = Array.from(ventilationTypes);
      
      // Calculate door undercut data
      const roomsWithDoors = rooms.filter((room: any) => room.doorPresent === true);
      const totalInternalDoorsCount = roomsWithDoors.length;
      
      // Only count "10mm or more" as having proper undercuts
      const roomsWithDoorUndercuts = roomsWithDoors.filter((room: any) => 
        room.doorUndercutSize && room.doorUndercutSize.toLowerCase().includes('10mm or more')
      );
      
      // Rooms without proper undercuts include: empty, "none", or "Less than 10mm"
      const roomsWithoutDoorUndercuts = roomsWithDoors
        .filter((room: any) => 
          !room.doorUndercutSize || 
          room.doorUndercutSize === '' || 
          room.doorUndercutSize.toLowerCase() === 'none' ||
          room.doorUndercutSize.toLowerCase().includes('less than')
        )
        .map((room: any) => room.name)
        .filter(Boolean);
      const roomsWithNoDoorUndercutsText = roomsWithoutDoorUndercuts.join(', ');
      
      // Determine if door undercuts are present in all doors
      const doorUndercutsPresentValue = totalInternalDoorsCount > 0 
        ? (roomsWithoutDoorUndercuts.length === 0 ? true : false)
        : null;
      
      console.log('🚪 [VentilationForm] Door undercut calculation:', {
        totalRooms: rooms.length,
        roomsWithDoors: roomsWithDoors.length,
        roomsWithDoorsList: roomsWithDoors.map((r: any) => ({ 
          name: r.name, 
          doorPresent: r.doorPresent, 
          doorUndercutSize: r.doorUndercutSize,
          hasProperUndercut: r.doorUndercutSize?.toLowerCase().includes('10mm or more')
        })),
        roomsWithProperUndercuts: roomsWithDoorUndercuts.length,
        roomsWithProperUndercutsList: roomsWithDoorUndercuts.map((r: any) => r.name),
        roomsWithoutProperUndercuts: roomsWithoutDoorUndercuts.length,
        roomsWithoutProperUndercutsList: roomsWithoutDoorUndercuts,
        roomsWithNoDoorUndercutsText,
        doorUndercutsPresentValue,
        currentFormValue: formData.doorUndercutsPresent
      });
      
      // Only update if values changed to prevent loops
      const ventilationTypesPresentString = JSON.stringify(ventilationTypesPresentArray);
      const currentVentilationTypesPresentString = JSON.stringify(formData.ventilationTypePresent);
      const ventilationTypeCountsString = JSON.stringify(typeCounts);
      const currentVentilationTypeCountsString = JSON.stringify(formData.ventilationTypeCounts);
      
      if (
        formData.totalRooms !== String(totalRoomsCount) ||
        formData.habitableRooms !== String(habitableRoomsCount) ||
        formData.heatedHabitableRooms !== String(heatedHabitableRoomsCount) ||
        formData.numberOfWindows !== String(totalWindowsCount) ||
        formData.numberOfTrickleVents !== String(totalTrickleVentsCount) ||
        formData.numberOfWetRooms !== String(wetRoomsCount) ||
        formData.numberOfVentilationFans !== String(ventilationFansCount) ||
        formData.roomsMissingVentilationFans !== roomsMissingVentilationFansText ||
        currentVentilationTypesPresentString !== ventilationTypesPresentString ||
        currentVentilationTypeCountsString !== ventilationTypeCountsString ||
        formData.doorUndercutsPresent !== doorUndercutsPresentValue ||
        formData.roomsWithNoDoorUndercuts !== roomsWithNoDoorUndercutsText
      ) {
        console.log('🚪 [VentilationForm] Form data WILL UPDATE - changes detected:', {
          doorUndercutsChanged: formData.doorUndercutsPresent !== doorUndercutsPresentValue,
          oldValue: formData.doorUndercutsPresent,
          newValue: doorUndercutsPresentValue,
          roomsListChanged: formData.roomsWithNoDoorUndercuts !== roomsWithNoDoorUndercutsText,
          oldRoomsList: formData.roomsWithNoDoorUndercuts,
          newRoomsList: roomsWithNoDoorUndercutsText
        });
        
        setFormData(prev => ({
          ...prev,
          totalRooms: String(totalRoomsCount),
          habitableRooms: String(habitableRoomsCount),
          heatedHabitableRooms: String(heatedHabitableRoomsCount),
          numberOfWindows: String(totalWindowsCount),
          numberOfTrickleVents: String(totalTrickleVentsCount),
          numberOfWetRooms: String(wetRoomsCount),
          numberOfVentilationFans: String(ventilationFansCount),
          roomsMissingVentilationFans: roomsMissingVentilationFansText,
          ventilationTypePresent: ventilationTypesPresentArray,
          ventilationTypeCounts: typeCounts,
          doorUndercutsPresent: doorUndercutsPresentValue,
          roomsWithNoDoorUndercuts: roomsWithNoDoorUndercutsText
        }));
        
        // Auto-save the calculated values
        if (onDataUpdate) {
          onDataUpdate(prev => ({
            ...prev,
            ventilation_condition: {
              ...prev.ventilation_condition,
              totalRooms: String(totalRoomsCount),
              habitableRooms: String(habitableRoomsCount),
              heatedHabitableRooms: String(heatedHabitableRoomsCount),
              numberOfWindows: String(totalWindowsCount),
              numberOfTrickleVents: String(totalTrickleVentsCount),
              numberOfWetRooms: String(wetRoomsCount),
              numberOfVentilationFans: String(ventilationFansCount),
              roomsMissingVentilationFans: roomsMissingVentilationFansText,
              ventilationTypePresent: ventilationTypesPresentArray,
              ventilationTypeCounts: typeCounts,
              doorUndercutsPresent: doorUndercutsPresentValue,
              roomsWithNoDoorUndercuts: roomsWithNoDoorUndercutsText
            }
          }), 'ventilation_condition');
        }
      }
    }
  }, [surveyData?.room_details?.rooms]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const isSelect = e.target instanceof HTMLSelectElement;
    
    // Mark recent change to prevent sync
    recentChangeRef.current = true;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Trigger autosave via parent callback
    if (onDataUpdate) {
      // Clear any existing timer
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }

      const triggerAutosave = () => {
        onDataUpdate(prev => ({
          ...prev,
          ventilation_condition: {
            ...prev.ventilation_condition,
            [name]: value
          }
        }), 'ventilation_condition');
        // Allow sync after autosave completes
        setTimeout(() => {
          recentChangeRef.current = false;
        }, 100);
      };

      // Debounce autosave for dropdowns (300ms after user stops changing)
      if (isSelect) {
        autosaveTimerRef.current = setTimeout(triggerAutosave, 300);
      } else {
        triggerAutosave();
      }
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, []);
  
  const handleNumericInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (/^\d*$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Trigger autosave via parent callback
        if (onDataUpdate) {
          onDataUpdate(prev => ({
            ...prev,
            ventilation_condition: {
              ...prev.ventilation_condition,
              [name]: value
            }
          }), 'ventilation_condition');
        }
    }
  };

  const handleBooleanChange = (field: keyof VentilationData, value: boolean) => {
    // Mark recent change to prevent sync
    recentChangeRef.current = true;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Trigger autosave via parent callback
    if (onDataUpdate) {
      onDataUpdate(prev => ({
        ...prev,
        ventilation_condition: {
          ...prev.ventilation_condition,
          [field]: value
        }
      }), 'ventilation_condition');
      // Allow sync after autosave completes
      setTimeout(() => {
        recentChangeRef.current = false;
      }, 100);
    }
  };

  const handlePhotosCaptured = (field: 'roofDefectImages' | 'dampnessImages' | 'wallDefectImages' | 'externalDoorsImages' | 'frontDoorOpenImage' | 'windowGlazingThicknessImage', capturedPhotos: PhotoData[]) => {
    const newImages: DisplayablePhotoData[] = capturedPhotos.map((p, idx) => {
      const displayId = `${field}_${Date.now()}_${idx}`;
      const dataUrl = `data:image/${p.format};base64,${p.base64String}`;

      // Queue photo for upload to CDN
      queuePhotoUpload(
        displayId,
        dataUrl,
        'ventilation_condition',
        field,
        (url) => {
          console.log(`✅ [VentilationCondition] Photo uploaded to CDN:`, url);
          // ✅ CRITICAL: Use explicit field selection, NO spread operator
          setFormData(prev => ({
            ...prev,
            [field]: prev[field].map((photo: DisplayablePhotoData) =>
              photo.displayId === displayId
                ? { 
                    displayId: photo.displayId,
                    format: photo.format,
                    fileName: photo.fileName,
                    url, // ✅ CDN URL
                    uploading: false,
                    error: undefined,
                    dataUrl: '', // ✅ Empty after upload
                    base64String: '' // ✅ Empty after upload
                  } as DisplayablePhotoData
                : photo
            )
          }));
          
          // Save to database with CDN URL
          if (onDataUpdate) {
            setTimeout(() => {
              setFormData(currentFormData => {
                console.log(`💾 [VentilationCondition] Saving photo with CDN URL`);
                onDataUpdate(prevData => ({
                  ...prevData,
                  ventilation_condition: {
                    ...prevData.ventilation_condition,
                    ...currentFormData
                  }
                }), 'ventilation_condition');
                return currentFormData;
              });
            }, 0);
          }
        },
        (error) => {
          // Mark upload as failed
          setFormData(prev => ({
            ...prev,
            [field]: prev[field].map((photo: DisplayablePhotoData) =>
              photo.displayId === displayId
                ? { ...photo, uploading: false, error }
                : photo
            )
          }));
        }
      );

      return {
        ...p,
        displayId,
        dataUrl,
        uploading: true,
      };
    });

    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], ...newImages],
    }));

    // Trigger autosave
    if (onDataUpdate) {
      onDataUpdate((prevData) => ({
        ...prevData,
        ventilation_condition: {
          ...prevData.ventilation_condition,
          [field]: [...((prevData.ventilation_condition as any)?.[field] || []), ...newImages],
        } as any,
      }));
    }
  };

  const removePhoto = async (field: 'roofDefectImages' | 'dampnessImages' | 'wallDefectImages' | 'externalDoorsImages' | 'frontDoorOpenImage' | 'windowGlazingThicknessImage', displayId: string) => {
    console.log(`[Ventilation] 🗑️ Removing photo from ${field}, displayId: ${displayId}`);
    
    // Find and delete from Supabase Storage
    const photoToDelete = formData[field].find((photo: DisplayablePhotoData) => photo.displayId === displayId);
    if (photoToDelete) {
      let filePath = (photoToDelete as any).fileName;
      if (!filePath && photoToDelete.url?.includes('/storage/v1/object/public/')) {
        filePath = photoToDelete.url.split('/storage/v1/object/public/')[1]?.split('/').slice(1).join('/');
      }
      if (filePath) {
        try {
          await deletePhotoFromSupabase(filePath, 'retrofit-survey-images');
          console.log(`[Ventilation] ✅ Deleted from storage: ${filePath}`);
        } catch (error) {
          console.warn(`[Ventilation] ⚠️ Failed to delete from storage:`, error);
        }
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((photo: DisplayablePhotoData) => photo.displayId !== displayId)
    }));

    // Trigger autosave
    if (onDataUpdate) {
      onDataUpdate(prevData => ({
        ...prevData,
        ventilation_condition: {
          ...prevData.ventilation_condition,
          [field]: ((prevData.ventilation_condition as any)?.[field] || []).filter((photo: any) => photo.displayId !== displayId)
        } as any
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Ventilation & Condition Data:', { floorPlanId, ...formData });
    onNavigateNext();
  };

  const handleOpenGallery = (e: React.MouseEvent, photoType: 'roofDefectImages' | 'dampnessImages' | 'wallDefectImages' | 'externalDoorsImages' | 'frontDoorOpenImage' | 'windowGlazingThicknessImage') => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedGallery(photoType);
  };

  const handleCloseGallery = () => {
    setSelectedGallery(null);
  };

  const renderPhotoPreview = (photoDatas: DisplayablePhotoData[], photoType: 'roofDefectImages' | 'dampnessImages' | 'wallDefectImages' | 'externalDoorsImages' | 'frontDoorOpenImage' | 'windowGlazingThicknessImage', altTextPrefix: string) => {
    if (!photoDatas || photoDatas.length === 0) return null;

    // Use CDN URL if available, otherwise use dataUrl
    const latestPhoto = photoDatas[photoDatas.length - 1];
    const imageUrl = latestPhoto.url || latestPhoto.dataUrl;
    
    if (!imageUrl) return null;

    return (
      <button
        type="button"
        onClick={(e) => handleOpenGallery(e, photoType)}
        className="relative w-20 h-20 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:scale-105 ring-2 ring-slate-300 hover:ring-blue-400 flex-shrink-0 mt-2"
      >
        <img
          src={imageUrl}
          alt={altTextPrefix}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-1 right-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
          <Images size={12} />
          {photoDatas.length}
        </div>
      </button>
    );
  };
  
  // --- STYLING ---
  const formContainerClass = "bg-white/95 backdrop-blur-sm p-6 sm:p-8 rounded-xl shadow-lg border border-yellow-200 min-h-[calc(100vh-12rem)] flex flex-col";
  const fieldsetWrapperClass = "border border-gray-200 rounded-lg p-4";
  const legendClass = "text-lg font-bold text-gray-800 px-2";
  const fieldWrapperClass = "py-3";
  const labelClass = "text-sm font-medium text-gray-800 flex items-center mb-2";
  const inputClass = "w-full p-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400";
  const requiredMarker = <span className="text-red-500 mr-1">*</span>;
  const booleanButtonClass = (isSelected: boolean | null, actualValue: boolean) =>
    `flex-1 py-3 rounded-lg border-2 ${isSelected === actualValue ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`;
  const photoButtonClass = "w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 transition-all duration-200";
  const photoCaptureContainerClass = "p-4 border border-gray-200 rounded-lg bg-gray-50/50 space-y-3 mt-2";

  return (
    <div className={formContainerClass}>
      <form onSubmit={handleSubmit} className="space-y-8 flex-1 flex flex-col">
        <div className="flex-1 space-y-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 border-b-2 border-gray-200 pb-4 flex items-center gap-3"><Wind />Ventilation and Condition Survey</h2>

            {/* --- Room Counts --- */}
            <fieldset className={fieldsetWrapperClass}>
              <legend className={legendClass}>Room Information</legend>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
                <div className={fieldWrapperClass}>
                  <label htmlFor="habitableRooms" className={labelClass}>
                    {requiredMarker}Number of Habitable Room?
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      id="habitableRooms"
                      name="habitableRooms"
                      value={formData.habitableRooms}
                      readOnly
                      className={`${inputClass} bg-blue-50 cursor-not-allowed`}
                      placeholder="Auto-calculated"
                      required
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 font-semibold">
                      Auto
                    </div>
                  </div>
                </div>
                <div className={fieldWrapperClass}>
                  <label htmlFor="heatedHabitableRooms" className={labelClass}>
                    {requiredMarker}Number of Heated Habitable Room?
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      id="heatedHabitableRooms"
                      name="heatedHabitableRooms"
                      value={formData.heatedHabitableRooms}
                      readOnly
                      className={`${inputClass} bg-blue-50 cursor-not-allowed`}
                      placeholder="Auto-calculated"
                      required
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 font-semibold">
                      Auto
                    </div>
                  </div>
                </div>
                <div className={fieldWrapperClass}>
                  <label htmlFor="totalRooms" className={labelClass}>
                    {requiredMarker}Number of Rooms including wet rooms?
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      id="totalRooms"
                      name="totalRooms"
                      value={formData.totalRooms}
                      readOnly
                      className={`${inputClass} bg-blue-50 cursor-not-allowed`}
                      placeholder="Auto-calculated"
                      required
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 font-semibold">
                      Auto
                    </div>
                  </div>
                </div>
              </div>
            </fieldset>

            {/* --- Windows --- */}
            <fieldset className={fieldsetWrapperClass}>
              <legend className={legendClass}>Windows</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                <div className={fieldWrapperClass}>
                  <label className={labelClass}>{requiredMarker}Condition of the window frames & glazing</label>
                  <select name="windowFramesCondition" value={formData.windowFramesCondition} onChange={handleInputChange} className={inputClass} required>{conditionOptions.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select>
                </div>
                <div className={fieldWrapperClass}>
                  <label className={labelClass}>{requiredMarker}Condition of draught proofing of all windows</label>
                  <select name="draughtProofingCondition" value={formData.draughtProofingCondition} onChange={handleInputChange} className={inputClass} required>{conditionOptions.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select>
                </div>
                <div className={fieldWrapperClass}>
                  <label className={labelClass}>{requiredMarker}Main Type of window frames</label>
                  <select name="windowFrameType" value={formData.windowFrameType} onChange={handleInputChange} className={inputClass} required>{windowFrameOptions.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select>
                </div>
                <div className={fieldWrapperClass}>
                  <label className={labelClass}>{requiredMarker}Number of windows present</label>
                  <div className="relative">
                    <input 
                      type="text" 
                      inputMode="numeric" 
                      name="numberOfWindows" 
                      value={formData.numberOfWindows} 
                      readOnly
                      className={`${inputClass} bg-blue-50 cursor-not-allowed`}
                      placeholder="Auto-calculated" 
                      required 
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 font-semibold">
                      Auto
                    </div>
                  </div>
                </div>
                <div className={fieldWrapperClass}>
                  <label className={labelClass}>{requiredMarker}Number of trickle vents present</label>
                  <div className="relative">
                    <input 
                      type="text" 
                      inputMode="numeric" 
                      name="numberOfTrickleVents" 
                      value={formData.numberOfTrickleVents} 
                      readOnly
                      className={`${inputClass} bg-blue-50 cursor-not-allowed`}
                      placeholder="Auto-calculated" 
                      required 
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 font-semibold">
                      Auto
                    </div>
                  </div>
                </div>
                <div className={fieldWrapperClass}>
                  <label className={labelClass}>{requiredMarker}Windows Glazing Type</label>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-2">
                    <label className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                      <input type="radio" name="windowGlazingType" value="single_glazing_pvc_metal" checked={formData.windowGlazingType === 'single_glazing_pvc_metal'} onChange={handleInputChange} className="form-radio text-yellow-500 focus:ring-yellow-400"/>
                      <span className="text-sm text-gray-700">Single Glazing PVC/Metal</span>
                    </label>
                    <label className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                      <input type="radio" name="windowGlazingType" value="single_glazing_wood" checked={formData.windowGlazingType === 'single_glazing_wood'} onChange={handleInputChange} className="form-radio text-yellow-500 focus:ring-yellow-400"/>
                      <span className="text-sm text-gray-700">Single Glazing Wood</span>
                    </label>
                    <label className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                      <input type="radio" name="windowGlazingType" value="double_glazing_pvc_metal" checked={formData.windowGlazingType === 'double_glazing_pvc_metal'} onChange={handleInputChange} className="form-radio text-yellow-500 focus:ring-yellow-400"/>
                      <span className="text-sm text-gray-700">Double Glazing PVC/Metal</span>
                    </label>
                    <label className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                      <input type="radio" name="windowGlazingType" value="double_glazing_wood" checked={formData.windowGlazingType === 'double_glazing_wood'} onChange={handleInputChange} className="form-radio text-yellow-500 focus:ring-yellow-400"/>
                      <span className="text-sm text-gray-700">Double Glazing Wood</span>
                    </label>
                    <label className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                      <input type="radio" name="windowGlazingType" value="triple_glazing_pvc_metal" checked={formData.windowGlazingType === 'triple_glazing_pvc_metal'} onChange={handleInputChange} className="form-radio text-yellow-500 focus:ring-yellow-400"/>
                      <span className="text-sm text-gray-700">Triple Glazing PVC/Metal</span>
                    </label>
                  </div>
                </div>
                <div className={fieldWrapperClass}>
                  <label className={labelClass}>{requiredMarker}Window Glazing Measurement</label>
                  <select name="windowGlazingMeasurement" value={formData.windowGlazingMeasurement} onChange={handleInputChange} className={inputClass} required>
                    <option value="">Select measurement</option>
                    <option value="6">6</option>
                    <option value="12">12</option>
                    <option value="16">16</option>
                  </select>
                </div>
                <div className={`${photoCaptureContainerClass} md:col-span-2`}>
                  <label className={labelClass}>{requiredMarker}Window Glazing Thickness Image</label>
                  <PhotoCapture onPhotosCaptured={(p) => handlePhotosCaptured('windowGlazingThicknessImage', p)} maxPhotos={999} buttonLabel="Capture Window Glazing Thickness" buttonClass={photoButtonClass} />
                  {renderPhotoPreview(formData.windowGlazingThicknessImage, 'windowGlazingThicknessImage', "Window Glazing Thickness")}
                </div>
              </div>
            </fieldset>

            {/* --- Doors --- */}
            <fieldset className={fieldsetWrapperClass}>
              <legend className={legendClass}>Doors</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                <div className={fieldWrapperClass}>
                  <label className={labelClass}>{requiredMarker}Number of External Doors?</label>
                  <input type="text" inputMode="numeric" name="numberOfExternalDoors" value={formData.numberOfExternalDoors} onChange={handleNumericInputChange} className={inputClass} placeholder="Enter number" required />
                </div>
                <div className={fieldWrapperClass}>
                  <label className={labelClass}>{requiredMarker}Condition of External Doors</label>
                  <select name="externalDoorsCondition" value={formData.externalDoorsCondition} onChange={handleInputChange} className={inputClass} required>{conditionOptions.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select>
                </div>
                 <div className={fieldWrapperClass}>
                  <label className={labelClass}>{requiredMarker}Type of External Doors</label>
                  <select name="externalDoorsType" value={formData.externalDoorsType} onChange={handleInputChange} className={inputClass} required>{externalDoorTypeOptions.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select>
                </div>
                <div className={fieldWrapperClass}>
                  <label className={labelClass}>{requiredMarker}Condition of draught proofing on the external door</label>
                  <select name="draughtProofingOnExternalDoor" value={formData.draughtProofingOnExternalDoor} onChange={handleInputChange} className={inputClass} required>{conditionOptions.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select>
                </div>
                <div className={`${fieldWrapperClass} md:col-span-2`}>
                  <label className={labelClass}>{requiredMarker}Is there door undercuts present in all doors? <span className="text-xs text-blue-600 font-semibold ml-2">Auto</span></label>
                  <div className="flex space-x-4 mt-2 bg-blue-50 p-3 rounded-lg">
                    <button type="button" disabled className={`${booleanButtonClass(formData.doorUndercutsPresent, true)} cursor-not-allowed opacity-75`}>Yes</button>
                    <button type="button" disabled className={`${booleanButtonClass(formData.doorUndercutsPresent, false)} cursor-not-allowed opacity-75`}>No</button>
                  </div>
                </div>
                {formData.doorUndercutsPresent === false && (
                    <div className={`${fieldWrapperClass} md:col-span-2`}>
                        <label className={labelClass}>Please specify the rooms with no door undercuts</label>
                        <div className="relative">
                          <input 
                            type="text" 
                            name="roomsWithNoDoorUndercuts" 
                            value={formData.roomsWithNoDoorUndercuts} 
                            readOnly
                            className={`${inputClass} bg-blue-50 cursor-not-allowed`}
                            placeholder="Auto-calculated" 
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 font-semibold">
                            Auto
                          </div>
                        </div>
                    </div>
                )}
                <div className={`${photoCaptureContainerClass} md:col-span-2`}>
                  <label className={labelClass}>{requiredMarker}Images of all external doors. (Showing Draught Proofing)</label>
                  <PhotoCapture onPhotosCaptured={(p) => handlePhotosCaptured('externalDoorsImages', p)} maxPhotos={999} buttonLabel="Capture External Doors Images" buttonClass={photoButtonClass} />
                  {renderPhotoPreview(formData.externalDoorsImages, 'externalDoorsImages', "External Doors")}
                </div>
                <div className={`${photoCaptureContainerClass} md:col-span-2`}>
                  <label className={labelClass}>{requiredMarker}Image of Front Door Open</label>
                  <PhotoCapture onPhotosCaptured={(p) => handlePhotosCaptured('frontDoorOpenImage', p)} maxPhotos={999} buttonLabel="Capture Front Door Open" buttonClass={photoButtonClass} />
                  {renderPhotoPreview(formData.frontDoorOpenImage, 'frontDoorOpenImage', "Front Door Open")}
                </div>
              </div>
            </fieldset>

            {/* --- Ventilation Fans --- */}
            <fieldset className={fieldsetWrapperClass}>
              <legend className={legendClass}>Ventilation Fans</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                 <div className={fieldWrapperClass}>
                  <label className={labelClass}>{requiredMarker}Number of wet rooms</label>
                  <div className="relative">
                    <input 
                      type="text" 
                      inputMode="numeric" 
                      name="numberOfWetRooms" 
                      value={formData.numberOfWetRooms} 
                      readOnly
                      className={`${inputClass} bg-blue-50 cursor-not-allowed`}
                      placeholder="Auto-calculated" 
                      required 
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 font-semibold">
                      Auto
                    </div>
                  </div>
                </div>
                <div className={fieldWrapperClass}>
                  <label className={labelClass}>{requiredMarker}Number of ventilation fans present</label>
                  <div className="relative">
                    <input 
                      type="text" 
                      inputMode="numeric" 
                      name="numberOfVentilationFans" 
                      value={formData.numberOfVentilationFans} 
                      readOnly
                      className={`${inputClass} bg-blue-50 cursor-not-allowed`}
                      placeholder="Auto-calculated" 
                      required 
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 font-semibold">
                      Auto
                    </div>
                  </div>
                </div>
                <div className="fieldWrapperClass md:col-span-2">
                  <label className={labelClass}>Rooms missing Ventilation Fans</label>
                  <div className="relative">
                    <input 
                      type="text" 
                      name="roomsMissingVentilationFans" 
                      value={formData.roomsMissingVentilationFans} 
                      readOnly
                      className={`${inputClass} bg-blue-50 cursor-not-allowed`}
                      placeholder="Auto-calculated" 
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 font-semibold">
                      Auto
                    </div>
                  </div>
                </div>
                <div className="fieldWrapperClass md:col-span-2">
                  <label className={labelClass}>Ventilation type present <span className="text-xs text-blue-600 font-semibold ml-2">Auto</span></label>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b-2 border-blue-300">
                          <th className="text-left py-2 px-3 text-sm font-semibold text-slate-700">Ventilation Type</th>
                          <th className="text-center py-2 px-3 text-sm font-semibold text-slate-700">Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-blue-200">
                          <td className="py-2 px-3 text-sm text-slate-600">Decentralised Mechanical Ventilation</td>
                          <td className="py-2 px-3 text-center text-sm font-medium text-slate-700">{formData.ventilationTypeCounts['decentralised_mechanical']}</td>
                        </tr>
                        <tr className="border-b border-blue-200">
                          <td className="py-2 px-3 text-sm text-slate-600">Intermittent Ventilation Fan</td>
                          <td className="py-2 px-3 text-center text-sm font-medium text-slate-700">{formData.ventilationTypeCounts['intermittent']}</td>
                        </tr>
                        <tr className="border-b border-blue-200">
                          <td className="py-2 px-3 text-sm text-slate-600">Centralised Mechanical Ventilation Fans</td>
                          <td className="py-2 px-3 text-center text-sm font-medium text-slate-700">{formData.ventilationTypeCounts['centralised_mechanical']}</td>
                        </tr>
                        <tr className="border-b border-blue-200">
                          <td className="py-2 px-3 text-sm text-slate-600">Other Ventilation Fans</td>
                          <td className="py-2 px-3 text-center text-sm font-medium text-slate-700">{formData.ventilationTypeCounts['other']}</td>
                        </tr>
                        <tr className="bg-blue-100 font-semibold">
                          <td className="py-2 px-3 text-sm text-slate-800">Total</td>
                          <td className="py-2 px-3 text-center text-sm font-bold text-blue-700">
                            {Object.values(formData.ventilationTypeCounts).reduce((sum, count) => sum + count, 0)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </fieldset>

            {/* --- Internal Walls --- */}
            <fieldset className={fieldsetWrapperClass}>
              <legend className={legendClass}>Internal Walls</legend>
              <div className="grid grid-cols-1">
                <div className={fieldWrapperClass}>
                  <label className={labelClass}>{requiredMarker}Condition of internal wall</label>
                  <select name="internalWallCondition" value={formData.internalWallCondition} onChange={handleInputChange} className={inputClass} required>{internalWallConditionOptions.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select>
                </div>
                <div className={fieldWrapperClass}>
                    <label className={labelClass}>{requiredMarker}Is there evidence of rising or penetrating dampness to the internal wall?</label>
                    <div className="flex space-x-4 mt-2">
                        <button type="button" onClick={() => handleBooleanChange('evidenceOfDamp', true)} className={booleanButtonClass(formData.evidenceOfDamp, true)}>Yes</button>
                        <button type="button" onClick={() => handleBooleanChange('evidenceOfDamp', false)} className={booleanButtonClass(formData.evidenceOfDamp, false)}>No</button>
                    </div>
                </div>
                {formData.evidenceOfDamp && (
                    <div className={photoCaptureContainerClass}>
                        <label className={labelClass}>{requiredMarker}Image of dampness on internal wall</label>
                        <PhotoCapture onPhotosCaptured={(p) => handlePhotosCaptured('dampnessImages', p)} maxPhotos={999} buttonLabel="Capture Dampness Images" buttonClass={photoButtonClass} />
                        {renderPhotoPreview(formData.dampnessImages, 'dampnessImages', "Dampness")}
                    </div>
                )}
              </div>
            </fieldset>

        </div>

        {/* --- Navigation --- */}
        <div className="mt-auto pt-8 border-t border-gray-200 flex flex-col sm:flex-row gap-4 sm:justify-between">
          <button type="button" onClick={onNavigateBack} className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 bg-gray-100 text-gray-700 font-semibold rounded-lg shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02]">
            <ArrowLeft size={18} /> Previous
          </button>
          <button type="submit" className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02]">
            Next <ArrowRight size={18} />
          </button>
        </div>
      </form>

      {/* Photo Gallery Modal */}
      {selectedGallery && (
        <PhotoGalleryModal
          isOpen={!!selectedGallery}
          onClose={handleCloseGallery}
          photos={formData[selectedGallery].map((photo: DisplayablePhotoData) => ({
            id: photo.displayId,
            url: photo.url || photo.dataUrl,
            localPath: photo.dataUrl,
            fileName: photo.fileName,
          }))}
          sectionTitle={
            selectedGallery === 'roofDefectImages' ? 'Roof Defect Images' :
            selectedGallery === 'dampnessImages' ? 'Dampness Images' :
            selectedGallery === 'wallDefectImages' ? 'Wall Defect Images' :
            selectedGallery === 'externalDoorsImages' ? 'External Doors Images' :
            selectedGallery === 'frontDoorOpenImage' ? 'Front Door Open Image' :
            selectedGallery === 'windowGlazingThicknessImage' ? 'Window Glazing Thickness Image' :
            'Photos'
          }
          onDeletePhoto={(photoId) => removePhoto(selectedGallery, photoId)}
          onUpdateNote={() => {}}
          onGalleryStateChange={onGalleryStateChange}
        />
      )}
    </div>
  );
};

export default VentilationConditionSurveyForm; 